import type { AxiosRequestConfig } from 'axios';
import z from 'zod';
import { api } from '../../axios.instance';
import {
  PagePaginationResponseDtoSchema,
  responseContract,
  SortDirection,
  type PagePaginationRequestDto,
} from '../../lib';

export enum PerformanceSortField {
  PERFORMANCE_SCORE = 'performanceScore',
  FACILITY_NAME = 'name',
  OCCUPANCY = 'occupancy',
  REVENUE_PER_RESIDENT = 'revenuePerResident',
  MARGIN = 'margin',
  AVERAGE_LOS = 'averageLos',
}

const PerformanceMetricsDtoSchema = z.object({
  occupancy: z.number().nullable(),
  revenuePerResident: z.number().nullable(),
  margin: z.number().nullable(),
  averageLos: z.number().nullable(),
  alertsResolutionEffectiveness: z.number().nullable(),
});

const PerformancePointsDtoSchema = z.object({
  occupancy: z.number(),
  revenuePerResident: z.number(),
  margin: z.number(),
  averageLos: z.number(),
  alertsResolutionEffectiveness: z.number(),
});

const FacilityPerformanceItemDtoSchema = z.object({
  id: z.number(),
  name: z.string(),
  performanceScore: z.number(),
  metrics: PerformanceMetricsDtoSchema,
  points: PerformancePointsDtoSchema,
});

const GetFacilitiesPerformanceResponseDtoSchema =
  PagePaginationResponseDtoSchema(FacilityPerformanceItemDtoSchema);

export type GetFacilitiesPerformanceRequestDto = PagePaginationRequestDto<
  {
    from: string;
    to: string;
  },
  PerformanceSortField
> & {
  sortDirection?: SortDirection;
  includeAllOrgFacilities?: boolean;
};

export type GetFacilitiesPerformanceResponseDto = z.infer<
  typeof GetFacilitiesPerformanceResponseDtoSchema
>;

export type FacilityPerformanceItemDto = z.infer<
  typeof FacilityPerformanceItemDtoSchema
>;

export const getFacilitiesPerformance = (
  params: GetFacilitiesPerformanceRequestDto,
  config?: AxiosRequestConfig<GetFacilitiesPerformanceRequestDto>
) => {
  return api
    .get('/facilities/performance', { params, ...config })
    .then(responseContract(GetFacilitiesPerformanceResponseDtoSchema));
};
